import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, ShoppingCart, User, Store, Mail, Clock, MapPin, Megaphone } from "lucide-react";
import { useCart } from "@/context/CartContext";
import api, { fileUrl } from "@/lib/api";

const LOGO = "/logo-acanthe-blanc.png";
const LOGO_DARK = fileUrl("acanthe-jardins/uploads/9d51122e-c3a5-43ba-9c94-828f93e29295.png");

const links = [
  { to: "/", label: "Accueil" },
  { to: "/prestations", label: "Prestations" },
  { to: "/realisations", label: "Réalisations" },
  { to: "/actualites", label: "Actualités" },
  { to: "/boutique", label: "Boutique", store: true },
  { to: "/entreprise", label: "L'entreprise" },
  { to: "/zone-intervention", label: "Zones" },
  { to: "/conseils", label: "Conseils & FAQ" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [info, setInfo] = useState(null);
  const loc = useLocation();
  const isHome = loc.pathname === "/";
  const { count } = useCart();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => { setOpen(false); }, [loc.pathname]);
  useEffect(() => { api.get("/site-info").then((r) => setInfo(r.data)).catch(() => {}); }, []);

  const solid = scrolled || !isHome;
  const showTopBar = info?.topbar_enabled;
  const shopOn = !!info?.shop_enabled;
  const navLinks = links.filter((l) => shopOn || !l.store);

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 inset-x-0 z-50"
    >
      {showTopBar && (
        <div className="hidden lg:block bg-forest text-cream/90 border-b border-white/10" data-testid="top-bar">
          <div className="max-w-7xl mx-auto px-6 md:px-12 h-9 flex items-center justify-between text-xs">
            <div className="flex items-center gap-5">
              {info.topbar_phone && <a href={`tel:${info.topbar_phone.replace(/\s/g, "")}`} className="flex items-center gap-1.5 font-bold text-sm text-cream hover:text-brand transition-colors" data-testid="top-phone"><Phone size={15} /> {info.topbar_phone}</a>}
              {info.topbar_phone2 && <a href={`tel:${info.topbar_phone2.replace(/\s/g, "")}`} className="flex items-center gap-1.5 hover:text-brand transition-colors">{info.topbar_phone2}</a>}
              {info.topbar_email && <a href={`mailto:${info.topbar_email}`} className="flex items-center gap-1.5 hover:text-brand transition-colors" data-testid="top-email"><Mail size={13} /> {info.topbar_email}</a>}
              {info.topbar_hours && <span className="flex items-center gap-1.5 text-cream/70" data-testid="top-hours"><Clock size={13} /> {info.topbar_hours}</span>}
              {info.topbar_address && <span className="hidden xl:flex items-center gap-1.5 text-cream/70"><MapPin size={13} /> {info.topbar_address}</span>}
            </div>
            <div className="flex items-center gap-4">
              {info.topbar_message_enabled && info.topbar_message && (
                <span className="flex items-center gap-1.5 rounded-full bg-terracotta/90 text-cream px-3 py-1 font-medium" data-testid="top-message"><Megaphone size={13} /> {info.topbar_message}</span>
              )}
              {info.topbar_show_devis && (
                <Link to="/contact" data-testid="top-devis" className="flex items-center gap-1.5 rounded-full bg-brand px-4 py-1 font-semibold text-cream hover:bg-white hover:text-forest transition-colors"><Phone size={12} /> Devis gratuit</Link>
              )}
            </div>
          </div>
        </div>
      )}

      <div
        className={`transition-all duration-500 ${
          solid ? "bg-cream/90 backdrop-blur-xl border-b border-border/60 py-3" : "bg-transparent py-5"
        }`}
      >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
        <Link to="/" data-testid="header-logo" onClick={() => window.scrollTo(0, 0)}>
          <img src={solid ? LOGO_DARK : LOGO} alt="Acanthe Jardins" className="h-12 md:h-12 w-auto" />
        </Link>
        <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
          {navLinks.map((l) => (
            l.store ? (
              <Link
                key={l.to}
                to={l.to}
                data-testid="nav-boutique"
                className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-bold tracking-wide transition-all ${
                  loc.pathname.startsWith("/boutique")
                    ? "bg-brand text-cream shadow-md shadow-brand/30"
                    : solid
                      ? "bg-brand/10 text-brand hover:bg-brand hover:text-cream"
                      : "bg-cream/15 text-cream ring-1 ring-cream/40 hover:bg-cream hover:text-brand backdrop-blur-sm"
                }`}
              >
                <Store size={16} /> {l.label}
              </Link>
            ) : (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z]/g, "")}`}
                className={`text-sm font-medium tracking-wide transition-colors hover:text-terracotta ${
                  loc.pathname === l.to ? "text-terracotta" : solid ? "text-forest" : "text-cream"
                }`}
              >
                {l.label}
              </Link>
            )
          ))}
          {shopOn && (
          <Link to="/boutique/panier" data-testid="header-cart" className={`relative p-2 ${solid ? "text-forest" : "text-cream"} hover:text-terracotta transition-colors`}>
            <ShoppingCart size={22} />
            {count > 0 && <span className="absolute -top-1 -right-1 bg-terracotta text-cream text-[10px] font-bold rounded-full h-5 w-5 flex items-center justify-center" data-testid="cart-count">{count}</span>}
          </Link>
          )}
          <Link to="/compte" data-testid="header-account" title="Mon compte" className={`flex items-center justify-center h-10 w-10 rounded-full transition-colors ${solid ? "bg-brand/10 text-brand hover:bg-brand hover:text-cream" : "bg-cream/15 text-cream ring-1 ring-cream/40 hover:bg-cream hover:text-brand backdrop-blur-sm"}`}>
            <User size={20} />
          </Link>
        </nav>
        <div className="flex items-center gap-1 lg:hidden">
          {info?.topbar_phone && (
            <a href={`tel:${info.topbar_phone.replace(/\s/g, "")}`} data-testid="header-phone-mobile"
              className={`flex items-center gap-1.5 rounded-full px-2.5 py-1.5 text-xs font-bold transition-colors ${solid ? "bg-brand/10 text-brand" : "bg-cream/15 text-cream ring-1 ring-cream/40 backdrop-blur-sm"}`}>
              <Phone size={15} /> <span className="hidden min-[360px]:inline">{info.topbar_phone}</span>
            </a>
          )}
          {shopOn && (
          <Link to="/boutique/panier" data-testid="header-cart-mobile" className={`relative p-2 ${solid ? "text-forest" : "text-cream"}`}>
            <ShoppingCart size={22} />
            {count > 0 && <span className="absolute -top-0.5 -right-0.5 bg-terracotta text-cream text-[10px] font-bold rounded-full h-4 w-4 flex items-center justify-center">{count}</span>}
          </Link>
          )}
          <button
            className="p-2"
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-btn"
            aria-label="Menu"
          >
            {open ? <X className={solid ? "text-forest" : "text-cream"} /> : <Menu className={solid ? "text-forest" : "text-cream"} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-cream border-t border-border mt-3 px-6 py-6 flex flex-col gap-4" data-testid="mobile-menu">
          {navLinks.map((l) => (
            <Link key={l.to} to={l.to} className="text-forest font-medium py-1" data-testid={`mobile-nav-${l.label.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z]/g, "")}`}>
              {l.label}
            </Link>
          ))}
          <Link to="/compte" className="text-forest font-medium py-1 flex items-center gap-2" data-testid="mobile-nav-compte"><User size={16} /> Mon compte</Link>
          <Link to="/contact" className="rounded-full bg-brand px-6 py-3 text-center text-cream font-semibold">
            Devis gratuit
          </Link>
        </div>
      )}
      </div>
    </header>
  );
}
